import { useEffect, useState } from 'react';
import type { A0SuggestionStep } from '../hooks/useA0Suggestions.js';

interface A0SuggestionsProps {
  step: A0SuggestionStep;
  /** Envía la opción tocada como turno del user (no-corregible, `fromSuggestion`). */
  onSend: (en: string) => void;
  /** El coach está hablando/pensando → los chips se deshabilitan (no spamear turnos). */
  busy: boolean;
}

/**
 * La RED de la charla guiada A0 (plan 33 §1.1): chips tap-or-speak del objetivo
 * pendiente. El alumno NUNCA enfrenta una página en blanco — siempre tiene la
 * salida a 1 tap (toca para enviar) y la muleta L1 (la glosa ES) a la vista.
 *
 * Recuperación NO temporizada (plan 33 §6 — un timeout automático es hostil para
 * tecnología asistiva): los chips están SIEMPRE visibles (la capa "Elegir" no se
 * saca nunca) y un botón persistente "¿No sabés qué decir?" agranda la ayuda en
 * español. La capa default sale del `stage` (§1.2): en `light` los chips arrancan
 * colapsados bajo "ver opciones" (el alumno intenta decirlo solo, con la red a un tap).
 */
export function A0Suggestions({ step, onSend, busy }: A0SuggestionsProps) {
  // En `light` la red arranca colapsada (el alumno ensaya decirlo solo). Se
  // re-evalúa cuando cambia el paso (nuevo objetivo) — de ahí el reset por key.
  const [open, setOpen] = useState(step.stage !== 'light');
  const [helpOpen, setHelpOpen] = useState(false);
  useEffect(() => {
    setOpen(step.stage !== 'light');
    setHelpOpen(false);
  }, [step.objectiveId, step.stage]);

  return (
    <div className="a0sug" role="group" aria-label="Sugerencias para responder">
      <div className="a0sug-goal">
        <span className="a0sug-goal-dot" aria-hidden="true" />
        {step.goalEs}
      </div>

      {step.stage === 'light' && !open ? (
        <button type="button" className="a0sug-reveal" onClick={() => setOpen(true)}>
          Decilo vos — o ver opciones
        </button>
      ) : (
        <div className="a0sug-chips">
          {step.suggestions.map((s) => (
            <button
              key={s.en}
              type="button"
              className="a0sug-chip"
              disabled={busy}
              onClick={() => onSend(s.en)}
            >
              <span className="a0sug-chip-en">{s.en}</span>
              <span className={`a0sug-chip-es${helpOpen ? ' is-help' : ''}`}>{s.es}</span>
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        className="a0sug-help"
        aria-pressed={helpOpen}
        onClick={() => {
          setHelpOpen((v) => !v);
          if (step.stage === 'light') setOpen(true);
        }}
      >
        {helpOpen ? 'Tocá una opción o decila 👆' : '¿No sabés qué decir?'}
      </button>
    </div>
  );
}
